import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

import { AppSettings } from '../app-settings';
import { HttpClientService } from './http-client.service';
import { CementingChoice, CementingDifficulty, CementingStatus } from '../models/cementing-status';

export interface QuizLaunchOptions {
  /** Template correlation id of the quiz to start: a retake of a finished quiz, or the next one by name. */
  templateCorrelationId?: string | null;
  /** True when the template names a finished quiz: Scholaranium starts it as a review-only retake. */
  retake?: boolean;
}

/**
 * Scholaranium cementing endpoints for the LMS course page (escholar
 * `CementingController`). One status call per subject feeds both the Medium
 * and the Hard module, in the journey view and the module view alike;
 * responses are cached per subject until `refresh` is called or a choice is
 * recorded.
 */
@Injectable()
export class CementingStatusService {
  private cache: { [subject: string]: Observable<CementingStatus | null> } = {};
  private changes = new Subject<CementingStatus>();

  /** Emits the refreshed status of a subject after a choice was recorded. */
  readonly statusChanges$ = this.changes.asObservable();

  constructor(private httpClientService: HttpClientService) {}

  /**
   * `GET {scholaraniumURL}/cementing/{subject}/status`.
   * Resolves to null when the subject has no cementing (empty body, `mode` null
   * or an error), so callers only have to test for null.
   */
  getStatus(subject: string): Observable<CementingStatus | null> {
    const key = this.normalizeSubject(subject);
    if (!key) { return of(null); }
    if (!this.cache[key]) {
      const url = AppSettings.scholaraniumURL + '/cementing/' + encodeURIComponent(key) + '/status';
      this.cache[key] = this.httpClientService.get(url, new HttpHeaders()).pipe(
        map((body: CementingStatus) => this.usable(body)),
        catchError(() => of(null)),
        shareReplay(1)
      );
    }
    return this.cache[key];
  }

  /** Drops the cached status so the next `getStatus` hits the server again. */
  refresh(subject?: string) {
    if (subject) {
      delete this.cache[this.normalizeSubject(subject)];
    } else {
      this.cache = {};
    }
  }

  /**
   * `POST {scholaraniumURL}/cementing/module/{categoryCorrelationId}/choice` with
   * `{"choice": "KEEP_GOING" | "STOP"}`. The server answers with the refreshed
   * status of the module's subject, which replaces the cache and is published on
   * `statusChanges$`. Null when the server refused the choice.
   */
  recordChoice(subject: string, categoryCorrelationId: string, choice: CementingChoice): Observable<CementingStatus | null> {
    const url = AppSettings.scholaraniumURL + '/cementing/module/' + encodeURIComponent(categoryCorrelationId) + '/choice';
    return this.httpClientService.post(url, { choice }, new HttpHeaders()).pipe(
      map((body: CementingStatus) => this.usable(body)),
      catchError(() => of(null)),
      tap(status => {
        const key = this.normalizeSubject(subject);
        if (status && key) {
          this.cache[key] = of(status);
          this.changes.next(status);
        } else {
          this.refresh(subject);
        }
      })
    );
  }

  /**
   * Scholaranium cementing page in embedded mode: with the module given it skips
   * its form, asks for Standard or Relaxed timing (minutes from the status API)
   * and shows the usual instructions. Without a template the backend serves the
   * next quiz of the module itself; with one it starts that template, as a
   * review-only retake when it was already finished.
   */
  quizLaunchUrl(section: string, subject: string, difficulty: CementingDifficulty, options: QuizLaunchOptions = {}): string {
    const params = [
      'subSection=' + encodeURIComponent(this.normalizeSubject(subject).replace(/-/g, '')),
      'dl=' + (difficulty === 'HARD' ? 'hard' : 'med'),
      'embedded=1'
    ];
    if (options.templateCorrelationId) {
      params.push('templateCorrelationId=' + encodeURIComponent(options.templateCorrelationId));
      if (options.retake) { params.push('retake=1'); }
    }
    return AppSettings.scholaraniumUiURL + '/quiz/cementing-quiz/' + encodeURIComponent(section) + '?' + params.join('&');
  }

  /** Existing Scholaranium result page for a finished cementing quiz, in embedded mode (framed by the course player). */
  reviewUrl(section: string, quizId: number): string {
    return AppSettings.scholaraniumUiURL + '/quiz/results/CQ/' + quizId + '/' + (section || '').toLowerCase() + '?embedded=1';
  }

  private usable(body: CementingStatus | null): CementingStatus | null {
    return body && body.mode && body.categories && body.categories.length ? body : null;
  }

  private normalizeSubject(subject: string): string {
    return (subject || '').trim().toUpperCase();
  }
}
